/**
 * 폰 로컬 Room ↔ SPA 메시지 브릿지 클라이언트 (privacy-first 재설계 D5).
 *
 * native가 `WebViewCompat.addWebMessageListener`로 주입한 `window.ButlerBridge`(postMessage +
 * message 이벤트)를 Promise/구독 API로 감싼다. native 프로토콜(WebBridge.kt)과 짝:
 *   request → {kind:'req',id,method,params} → {kind:'res',id,ok,data|error}
 *   subscribe → {kind:'sub',id,...} → {kind:'evt',id,ok,data|error} (Room Flow 변경마다)
 *   unsubscribe → {kind:'unsub',id}
 *
 * Kotlin shell이 없는 브라우저(개발용)에선 빈 데이터로 graceful fallback.
 */

interface NativePort {
  postMessage(message: string): void;
  addEventListener?(type: 'message', listener: (e: MessageEvent) => void): void;
  onmessage?: ((e: MessageEvent) => void) | null;
}

declare global {
  interface Window {
    ButlerBridge?: NativePort;
  }
}

export interface RoomBridge {
  request<T = unknown>(method: string, params?: Record<string, unknown>): Promise<T>;
  subscribe<T = unknown>(
    method: string,
    params: Record<string, unknown>,
    onData: (data: T) => void,
    onError?: (error: string) => void,
  ): () => void;
}

const REQUEST_TIMEOUT_MS = 15_000;

type Pending = { resolve: (v: unknown) => void; reject: (e: Error) => void };
type Sub = { onData: (d: unknown) => void; onError?: (e: string) => void };

class NativeRoomBridge implements RoomBridge {
  private seq = 0;
  private readonly pending = new Map<string, Pending>();
  private readonly subs = new Map<string, Sub>();

  constructor(private readonly port: NativePort) {
    const onMsg = (e: MessageEvent) => this.onMessage(e);
    if (port.addEventListener) port.addEventListener('message', onMsg);
    else port.onmessage = onMsg;
  }

  private nextId(): string {
    this.seq += 1;
    return `b${this.seq}`;
  }

  private onMessage(e: MessageEvent): void {
    let msg: { kind?: string; id?: string; ok?: boolean; data?: unknown; error?: string };
    try {
      msg = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
    } catch {
      return;
    }
    const id = msg.id ?? '';
    if (msg.kind === 'res') {
      const p = this.pending.get(id);
      if (!p) return;
      this.pending.delete(id);
      if (msg.ok) p.resolve(msg.data);
      else p.reject(new Error(msg.error ?? 'bridge error'));
    } else if (msg.kind === 'evt') {
      const s = this.subs.get(id);
      if (!s) return;
      if (msg.ok) s.onData(msg.data);
      else s.onError?.(msg.error ?? 'bridge error');
    }
  }

  request<T>(method: string, params: Record<string, unknown> = {}): Promise<T> {
    const id = this.nextId();
    return new Promise<T>((resolve, reject) => {
      // 타임아웃 — native가 응답을 못 주는 경우(페이지 리로드로 in-flight req 유실, 핸들러 hang 등)
      // pending이 영원히 남아 화면이 무한 로딩되는 걸 방지. 응답 오면 clear.
      const timer = setTimeout(() => {
        if (this.pending.delete(id)) reject(new Error(`bridge request timeout: ${method}`));
      }, REQUEST_TIMEOUT_MS);
      this.pending.set(id, {
        resolve: (v) => {
          clearTimeout(timer);
          resolve(v as T);
        },
        reject: (e) => {
          clearTimeout(timer);
          reject(e);
        },
      });
      this.port.postMessage(JSON.stringify({ kind: 'req', id, method, params }));
    });
  }

  subscribe<T>(
    method: string,
    params: Record<string, unknown>,
    onData: (data: T) => void,
    onError?: (error: string) => void,
  ): () => void {
    const id = this.nextId();
    this.subs.set(id, { onData: onData as (d: unknown) => void, onError });
    this.port.postMessage(JSON.stringify({ kind: 'sub', id, method, params }));
    return () => {
      if (!this.subs.has(id)) return;
      this.subs.delete(id);
      this.port.postMessage(JSON.stringify({ kind: 'unsub', id }));
    };
  }
}

/**
 * 브라우저 개발용 — native 브릿지 없을 때 샘플 데이터(Kotlin DebugSeeder와 동일 모양)로 응답.
 * 폰 없이 SPA를 dev 서버에서 띄워도 채워진 UI를 확인할 수 있게 한다. 매칭 메서드 외엔 빈값.
 */
const DEV_NOW = Date.now();
const DEV_CALLS = [
  {
    id: 'seed-call-1',
    startedAt: DEV_NOW - 2 * 3600_000,
    durationSec: 312,
    contactPhone: '+821022223333',
    contactName: '김부장 (한빛상사)',
    direction: 'in',
    category: 'work',
    status: 'ready',
    error: null,
  },
  {
    id: 'seed-call-2',
    startedAt: DEV_NOW - 20 * 60_000,
    durationSec: 88,
    contactPhone: '+821055556666',
    contactName: null,
    direction: 'out',
    category: null,
    status: 'summarizing',
    error: null,
  },
  {
    id: 'seed-call-3',
    startedAt: DEV_NOW - 2 * 60_000,
    durationSec: 45,
    contactPhone: '+821077778888',
    contactName: '이과장',
    direction: 'in',
    category: null,
    status: 'captured',
    error: null,
  },
];
const DEV_DETAIL: Record<string, unknown> = {
  'seed-call-1': {
    call: DEV_CALLS[0],
    transcript: {
      id: 'seed-tr-1',
      fullText: '여보세요 한빛상사 김부장입니다. 네 부장님 지난번 견적 건으로 연락드렸습니다. 단가를 조금만 더 조정해주시면 이번 주 안에 계약할게요.',
      speakerSegments: [
        { speaker: '1', start: 0, end: 4.2, text: '여보세요, 한빛상사 김부장입니다.' },
        { speaker: '2', start: 4.5, end: 9, text: '네 부장님, 지난번 견적 건으로 연락드렸습니다.' },
        { speaker: '1', start: 9.2, end: 15, text: '단가를 조금만 더 조정해주시면 이번 주 안에 계약할게요.' },
      ],
      provider: 'clova',
    },
    summary: {
      id: 'seed-sum-1',
      topic: '한빛상사 견적 단가 협의',
      bullets: ['김부장이 견적 단가 추가 조정 요청', '조정 시 이번 주 내 계약 의사 밝힘'],
      followups: ['수정 견적서 재발송', '이번 주 내 계약 일정 확인'],
      model: 'claude-haiku-4-5',
    },
  },
  'seed-call-2': { call: DEV_CALLS[1], transcript: null, summary: null },
  'seed-call-3': { call: DEV_CALLS[2], transcript: null, summary: null },
};
const DEV_SMS = [
  {
    id: 'seed-sms-1',
    receivedAt: DEV_NOW - 30 * 60_000,
    fromPhone: '+821022223333',
    body: '부장님, 견적서 메일로 보내주시면 오늘 검토하고 회신드릴게요.',
    direction: 'in',
  },
  {
    id: 'seed-sms-2',
    receivedAt: DEV_NOW - 3 * 3600_000,
    fromPhone: '+821088889999',
    body: '[Web발신] 카드 승인 12,000원 스타벅스',
    direction: 'in',
  },
];
const DEV_TODOS = [
  {
    id: 'seed-todo-1',
    sourceType: 'call',
    sourceId: 'seed-call-1',
    content: '한빛상사 수정 견적서 재발송',
    dueAt: DEV_NOW + 24 * 3600_000,
    status: 'new',
    confidence: 0.9,
    createdAt: DEV_NOW - 2 * 3600_000,
    updatedAt: DEV_NOW - 2 * 3600_000,
  },
];

class DevRoomBridge implements RoomBridge {
  private data(method: string, params: Record<string, unknown>): unknown {
    // 샘플 시드는 dev 빌드에서만. prod 빌드에선 빈 데이터(가짜 통화가 운영에 새지 않도록).
    if (!import.meta.env.DEV) return method.endsWith('.list') ? [] : null;
    switch (method) {
      case 'calls.list':
        return DEV_CALLS;
      case 'calls.get':
        return DEV_DETAIL[String(params.id)] ?? null;
      case 'todos.list':
        return DEV_TODOS;
      case 'sms.list':
        return DEV_SMS;
      case 'chat.send':
        return { threadId: 'dev-thread' };
      default:
        return method.endsWith('.list') ? [] : null;
    }
  }
  request<T>(method: string, params: Record<string, unknown> = {}): Promise<T> {
    return Promise.resolve(this.data(method, params) as T);
  }
  subscribe<T>(method: string, params: Record<string, unknown>, onData: (data: T) => void): () => void {
    setTimeout(() => onData(this.data(method, params) as T), 0);
    return () => {};
  }
}

let instance: RoomBridge | null = null;

export function roomBridge(): RoomBridge {
  if (instance) return instance;
  if (typeof window === 'undefined') throw new Error('roomBridge() called in non-browser env');
  instance = window.ButlerBridge ? new NativeRoomBridge(window.ButlerBridge) : new DevRoomBridge();
  return instance;
}

/** 통화 파일럿 편의 래퍼 — native WebBridge에 등록된 메서드와 1:1. */
export const room = {
  calls: {
    list: (limit = 100) => roomBridge().request<unknown[]>('calls.list', { limit }),
    observe: (cb: (calls: unknown[]) => void) => roomBridge().subscribe<unknown[]>('calls.list', { limit: 100 }, cb),
    get: (id: string) => roomBridge().request('calls.get', { id }),
    observeOne: (id: string, cb: (detail: unknown) => void) => roomBridge().subscribe('calls.get', { id }, cb),
  },
  todos: {
    list: (status?: string) => roomBridge().request<unknown[]>('todos.list', status ? { status } : {}),
    observe: (cb: (todos: unknown[]) => void, status?: string) =>
      roomBridge().subscribe<unknown[]>('todos.list', status ? { status } : {}, cb),
    update: (id: string, status: string) => roomBridge().request('todos.update', { id, status }),
  },
  suggestions: {
    observe: (cb: (rows: unknown[]) => void, status?: string) =>
      roomBridge().subscribe<unknown[]>('suggestions.list', status ? { status } : {}, cb),
  },
  sms: {
    list: (limit = 200) => roomBridge().request<unknown[]>('sms.list', { limit }),
    observe: (cb: (rows: unknown[]) => void, limit = 200) =>
      roomBridge().subscribe<unknown[]>('sms.list', { limit }, cb),
  },
  chat: {
    observeThreads: (cb: (rows: unknown[]) => void) => roomBridge().subscribe<unknown[]>('chat.threads', {}, cb),
    observeMessages: (threadId: string, cb: (rows: unknown[]) => void) =>
      roomBridge().subscribe<unknown[]>('chat.messages', { threadId }, cb),
    send: (text: string, threadId?: string) =>
      roomBridge().request<{ threadId: string }>('chat.send', threadId ? { threadId, text } : { text }),
  },
  privacyLedger: {
    observe: (cb: (rows: unknown[]) => void, limit = 200) =>
      roomBridge().subscribe<unknown[]>('privacyLedger.list', { limit }, cb),
  },
};
